import { useEffect, useId, useRef, useState, type RefObject } from 'react';

import type {
  PlaybackRate,
  VideoPlayerHandle,
  VideoPlayerSnapshot,
  YouTubePlayerInstance,
} from './player.types';
import { PLAYER_STATES } from './player.types';
import { useYouTubeApi } from './useYouTubeApi';
import styles from './YouTubePlayer.module.css';

interface YouTubePlayerProps {
  className?: string;
  onError?: (message: string | null) => void;
  onHandleChange?: (handle: VideoPlayerHandle | null) => void;
  onSnapshotChange?: (snapshot: VideoPlayerSnapshot) => void;
  onStateChange?: (playerState: number) => void;
  videoId: string | null;
}

function clampVolume(volume: number): number {
  return Math.min(100, Math.max(0, Math.round(volume)));
}

function readPlaybackRate(player: YouTubePlayerInstance): PlaybackRate {
  const rate = player.getPlaybackRate();
  return (rate || 1) as PlaybackRate;
}

function createHandle(
  playerRef: RefObject<YouTubePlayerInstance | null>,
): VideoPlayerHandle {
  return {
    getCurrentTime: () => playerRef.current?.getCurrentTime() ?? 0,
    getDuration: () => playerRef.current?.getDuration() ?? 0,
    getPlaybackRate: () => playerRef.current?.getPlaybackRate() ?? 1,
    getPlayerState: () => playerRef.current?.getPlayerState() ?? PLAYER_STATES.UNSTARTED,
    getVolume: () => playerRef.current?.getVolume() ?? 100,
    isMuted: () => playerRef.current?.isMuted() ?? false,
    loadVideoById: (videoId) => {
      playerRef.current?.loadVideoById(videoId);
    },
    mute: () => {
      playerRef.current?.mute();
    },
    pause: () => {
      playerRef.current?.pauseVideo();
    },
    play: () => {
      playerRef.current?.playVideo();
    },
    seek: (seconds) => {
      playerRef.current?.seekTo(seconds, true);
    },
    setPlaybackRate: (rate) => {
      playerRef.current?.setPlaybackRate(rate);
    },
    setVolume: (volume) => {
      playerRef.current?.setVolume(clampVolume(volume));
    },
    unMute: () => {
      playerRef.current?.unMute();
    },
  };
}

export function YouTubePlayer({
  className,
  onError,
  onHandleChange,
  onSnapshotChange,
  onStateChange,
  videoId,
}: YouTubePlayerProps) {
  const api = useYouTubeApi();
  const containerId = useId().replace(/:/g, '-');
  const playerContainerId = `youtube-player-${containerId}`;
  const hostRef = useRef<HTMLDivElement | null>(null);
  const latestVideoIdRef = useRef<string | null>(null);
  const onErrorRef = useRef(onError);
  const onHandleChangeRef = useRef(onHandleChange);
  const onSnapshotChangeRef = useRef(onSnapshotChange);
  const onStateChangeRef = useRef(onStateChange);
  const playerRef = useRef<YouTubePlayerInstance | null>(null);
  const [playerReady, setPlayerReady] = useState(false);

  useEffect(() => {
    onErrorRef.current = onError;
  }, [onError]);

  useEffect(() => {
    onHandleChangeRef.current = onHandleChange;
  }, [onHandleChange]);

  useEffect(() => {
    onSnapshotChangeRef.current = onSnapshotChange;
  }, [onSnapshotChange]);

  useEffect(() => {
    onStateChangeRef.current = onStateChange;
  }, [onStateChange]);

  function emitSnapshot(player: YouTubePlayerInstance) {
    onSnapshotChangeRef.current?.({
      currentTime: player.getCurrentTime() || 0,
      duration: player.getDuration() || 0,
      isMuted: player.isMuted(),
      playbackRate: readPlaybackRate(player),
      playerState: player.getPlayerState() ?? PLAYER_STATES.UNSTARTED,
      volume: clampVolume(player.getVolume() || 0),
    });
  }

  useEffect(() => {
    if (api.status === 'error') {
      onErrorRef.current?.(api.errorMessage ?? 'Unable to load the YouTube player.');
      onHandleChangeRef.current?.(null);
      onStateChangeRef.current?.(PLAYER_STATES.UNSTARTED);
      return;
    }

    onErrorRef.current?.(null);
  }, [api.errorMessage, api.status]);

  useEffect(() => {
    if (!api.isReady || !videoId || !hostRef.current || playerRef.current) {
      return;
    }

    const youtubeApi = window.YT;
    let playerInstance: YouTubePlayerInstance | null = null;

    if (!youtubeApi?.Player) {
      onErrorRef.current?.('The YouTube player API is not available.');
      return;
    }

    playerInstance = new youtubeApi.Player(playerContainerId, {
      events: {
        onError: () => {
          onErrorRef.current?.('Unable to load the selected YouTube video.');
        },
        onReady: (event) => {
          playerRef.current = event.target;
          latestVideoIdRef.current = videoId;
          setPlayerReady(true);
          onHandleChangeRef.current?.(createHandle(playerRef));
          onStateChangeRef.current?.(event.target.getPlayerState());
          emitSnapshot(event.target);
        },
        onStateChange: (event) => {
          onStateChangeRef.current?.(event.data);
          emitSnapshot(event.target);
        },
      },
      playerVars: {
        controls: 0,
        enablejsapi: 1,
        modestbranding: 1,
        origin: window.location.origin,
        playsinline: 1,
        rel: 0,
      },
      videoId,
    });

    return () => {
      const readyPlayer = playerRef.current;

      onHandleChangeRef.current?.(null);
      onStateChangeRef.current?.(PLAYER_STATES.UNSTARTED);
      setPlayerReady(false);
      latestVideoIdRef.current = null;

      readyPlayer?.destroy();
      playerRef.current = null;
      if (playerInstance && playerInstance !== readyPlayer) {
        playerInstance.destroy();
      }
      playerInstance = null;
    };
  }, [api.isReady, videoId]);

  useEffect(() => {
    if (!videoId || !playerReady || !playerRef.current) {
      return;
    }

    if (latestVideoIdRef.current === videoId) {
      return;
    }

    latestVideoIdRef.current = videoId;
    playerRef.current?.loadVideoById(videoId);

    if (playerRef.current) {
      emitSnapshot(playerRef.current);
    }
  }, [playerReady, videoId]);

  useEffect(() => {
    if (!playerReady || !playerRef.current) {
      return;
    }

    const intervalId = window.setInterval(() => {
      if (!playerRef.current) {
        return;
      }

      const playerState = playerRef.current.getPlayerState();

      if (
        playerState === PLAYER_STATES.PLAYING ||
        playerState === PLAYER_STATES.BUFFERING
      ) {
        emitSnapshot(playerRef.current);
      }
    }, 250);

    return () => {
      window.clearInterval(intervalId);
    };
  }, [playerReady]);

  return (
    <div className={className}>
      {/* The name is on the shell, which has a role: YouTube replaces the
          host below with its frame, and a name on a div with no role is one
          a screen reader may never read (#173). */}
      <div aria-label="Lesson video player" className={styles.shell} role="group">
        <div
          className={styles.frame}
          id={playerContainerId}
          ref={hostRef}
        />
        {!videoId ? (
          <div className={styles.overlay} role="status">
            <p>No section video IDs were provided for this component.</p>
          </div>
        ) : null}
        {videoId && api.status === 'loading' ? (
          <div className={styles.overlay} role="status">
            <p>Loading video player…</p>
          </div>
        ) : null}
        {videoId && api.status === 'error' ? (
          <div className={styles.overlay} role="alert">
            <p>{api.errorMessage ?? 'Unable to load the YouTube player.'}</p>
          </div>
        ) : null}
      </div>
    </div>
  );
}
